import React, { useState, useMemo } from 'react';
import {
  CloudCog, Smartphone, Layers, RefreshCw, Eye, Maximize2,
  BarChart3, LayoutTemplate, Database, Cpu, Share2, Clock,
  Wind, CheckCircle2, XCircle, Wallet, Zap, Calendar
} from 'lucide-react';
import { theme } from '../../utils/theme';
import { useLanguage } from '../../contexts/LanguageContext';
import { Reveal } from '../ui-custom/Reveal';
import { Slider } from '../ui/slider';

// --- SOUS-COMPOSANTS ---

const ComparisonItem = ({ icon, label, sailec, store, storeOk, film, filmOk, windowItem, windowOk }) => {
  return (
    <div className="grid grid-cols-5 gap-4 items-center py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors rounded-lg px-4 min-w-[800px] lg:min-w-0">
      <div className="col-span-1 text-gray-400 font-medium flex items-center gap-3">
        {icon} <span>{label}</span>
      </div>
      <div className="col-span-1 font-bold flex items-start gap-2 text-white">
        <CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" />
        <span className="text-sm">{sailec}</span>
      </div>
      <div className="col-span-1 text-gray-500 text-sm flex items-start gap-2">
        {storeOk ? (
          <CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" />
        ) : (
          <XCircle size={16} className="text-red-900/50 shrink-0 mt-0.5" />
        )}
        <span>{store}</span>
      </div>
      <div className="col-span-1 text-gray-500 text-sm flex items-start gap-2">
        {filmOk ? (
          <CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" />
        ) : (
          <XCircle size={16} className="text-red-900/50 shrink-0 mt-0.5" />
        )}
        <span>{film}</span>
      </div>
      <div className="col-span-1 text-gray-500 text-sm flex items-start gap-2">
        {windowOk ? (
          <CheckCircle2 size={16} className="text-green-500 shrink-0 mt-0.5" />
        ) : (
          <XCircle size={16} className="text-red-900/50 shrink-0 mt-0.5" />
        )}
        <span>{windowItem}</span>
      </div>
    </div>
  );
};

const PlaceholderScreen = ({ title, subtitle, color, icon, imageSrc }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <div className={`w-full h-full aspect-[9/19] rounded-3xl border-2 ${color} bg-[#0a0a0a] flex flex-col items-center justify-center p-4 relative overflow-hidden group hover:bg-[#111] transition-colors`} style={{ borderColor: color }}>
      {imageSrc && !imageError ? (
        <img
          src={imageSrc}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover rounded-3xl opacity-90"
          onError={() => setImageError(true)}
        />
      ) : (
        <>
          <div className="text-gray-600 mb-2 group-hover:text-white transition-colors duration-500 relative z-10">
            {icon || <Smartphone size={32} />}
          </div>
          <div className="text-center relative z-10">
            <div className="text-gray-300 font-bold text-sm mb-1">{title}</div>
            <div className="text-gray-600 text-xs uppercase tracking-wider">{subtitle}</div>
          </div>
          <div className="absolute bottom-4 text-[10px] text-gray-700 font-mono border border-gray-800 px-2 py-1 rounded z-10">PLACEHOLDER</div>
        </>
      )}
    </div>
  );
};

const ApiCard = ({ icon, title, desc, highlight }) => {
  return (
    <Reveal>
      <div className={`p-6 rounded-2xl border h-full flex flex-col ${highlight ? 'bg-teal-900/10 ' : 'bg-[#111] border-white/5'}`} style={{ borderColor: highlight ? theme.colors.primary : undefined }}>
        <div className={`mb-4 ${highlight ? '' : 'text-gray-400'}`} style={{ color: highlight ? theme.colors.primary : undefined }}>{React.cloneElement(icon, { size: 32 })}</div>
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
      </div>
    </Reveal>
  );
};

// Step Card with hover description
const StepCard = ({ step, t, isHovered, onHover, onLeave, anyHovered }) => {
  const stepDescriptions = [
    t.solutionPage.step1Desc,
    t.solutionPage.step2Desc,
    t.solutionPage.step3Desc,
    t.solutionPage.step4Desc,
    t.solutionPage.step5Desc
  ];

  return (
    <Reveal delay={step * 100}>
      <div
        className={`flex flex-col gap-3 transition-all duration-300 ${anyHovered && !isHovered ? 'opacity-30 scale-95' : 'opacity-100 scale-100'}`}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
      >
        <PlaceholderScreen
          title={`${t.solutionPage.step} ${step}`}
          subtitle={t.solutionPage.config}
          color={theme.colors.border}
          imageSrc={`/images/app-screenshots/step-${step}.jpg`}
        />
        <span className="text-xs text-center text-gray-500 font-mono">STEP 0{step}</span>

        {/* Description tooltip on hover */}
        <div className={`transition-all duration-300 overflow-hidden ${isHovered ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0'}`} style={{ color: theme.colors.secondary }}>
          <p className="text-xs  text-center px-2 py-2 bg-teal-900/20 rounded-lg border border-teal-500/20" >
            {stepDescriptions[step - 1]}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

const SavingsComparisonChart = ({ solutions, t, duration }) => {
  // The value to plot is now roiPercentageForDuration
  // Let's cap infinite ROI for visualization purposes.
  const cappedMaxValue = 200; // Max % to show for infinity, to keep the graph readable

  solutions.forEach(s => {
    if (s.roiPercentageForDuration === Infinity) {
      s.displayValue = cappedMaxValue;
      s.isInfinite = true;
    } else {
      s.displayValue = s.roiPercentageForDuration;
      s.isInfinite = false;
    }
  });

  const values = solutions.map(s => s.displayValue);

  const maxValue = Math.max(0.01, ...values.filter(v => v > 0));
  const minValue = Math.min(-0.01, ...values.filter(v => v < 0));
  const totalRange = maxValue - minValue;

  // Position of the zero line, from the top (0% to 100%)
  const zeroLinePosition = totalRange > 0 ? (maxValue / totalRange) * 100 : 50;

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-[#111] min-h-[450px] flex flex-col">
      <h4 className="text-xl font-bold text-white mb-2">{t.solutionPage.chartRoiTitle?.replace('{years}', duration) || `Retour sur Investissement sur ${duration} ans`}</h4>
      <p className="text-sm text-gray-400 mb-8">{t.solutionPage.chartRoiSubtitle || "ROI (%) = (Gain net / Coût d'investissement) × 100"}</p>

      <div className="flex-grow w-full relative pt-6 pb-10">
        {/* Y-Axis labels */}
        <div className="absolute -left-2 top-4 text-xs text-gray-500">{maxValue > 0 && `+${maxValue.toFixed(0)}%`}</div>
        <div className="absolute -left-2 bottom-8 text-xs text-gray-500">{minValue < 0 && `${minValue.toFixed(0)}%`}</div>

        {/* Graph Area */}
        <div className="absolute inset-x-4 top-6 bottom-10">
          {/* Zero Line */}
          <div className="absolute w-full h-px bg-gray-600 border-t border-dashed border-gray-700 z-0" style={{ top: `${zeroLinePosition}%` }}>
            <span className="absolute right-full top-0 -translate-y-1/2 pr-2 text-xs text-gray-500">0%</span>
          </div>

          {/* Bars */}
          <div className="absolute inset-0 grid grid-cols-5 gap-4 sm:gap-6 md:gap-8 z-10">
            {solutions.map((sol, index) => {
              const value = sol.displayValue;
              const isPositive = value >= 0;

              const barColor = sol.isFeatured
                ? (isPositive ? theme.colors.secondary : theme.colors.primary)
                : (isPositive ? '#16a34a' : '#dc2626');

              let height, top;
              if (isPositive) {
                height = totalRange > 0 ? (value / totalRange) * 100 : 0;
                top = zeroLinePosition - height;
              } else {
                height = totalRange > 0 ? (Math.abs(value) / totalRange) * 100 : 0;
                top = zeroLinePosition;
              }

              return (
                <div key={sol.id} className="relative flex flex-col items-center justify-center group">
                  {/* The bar */}
                  <div
                    className="absolute w-full rounded-md transition-all duration-1000 ease-out"
                    style={{
                      height: `${height}%`,
                      top: `${top}%`,
                      backgroundColor: barColor,
                      transitionDelay: `${index * 100}ms`
                    }}
                  >
                    {/* Value on hover */}
                    <div className={`absolute -top-7 w-full text-center text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                      {sol.isInfinite ? '∞' : `${sol.roiPercentageForDuration.toFixed(0)}%`}
                    </div>
                  </div>

                  {/* Label */}
                  <p className="absolute -bottom-8 text-xs text-center text-gray-400 h-8 w-full">{sol.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const SavingsCalculatorSection = ({ t }) => {
  const [numWindows, setNumWindows] = useState([12]);
  const [monthlyBill, setMonthlyBill] = useState([400]);
  const [roiDuration, setRoiDuration] = useState([10]);

  const solutionsData = useMemo(() => ([
    {
      id: 'sailec',
      name: 'SailecTech',
      eff: 0.1117,
      cpp: 400,
      isFeatured: true,
    },
    {
      id: 'smartWindow',
      name: t.solutionPage.smartWindowName || 'Fenêtre intelligente',
      eff: 0.18,
      cpp: 1850,
      isFeatured: false,
    },
    {
      id: 'summerFilm',
      name: t.solutionPage.summerFilmName || 'Film solaire été',
      eff: 0.069,
      cpp: 400,
      isFeatured: false,
    },
    {
      id: 'winterFilm',
      name: t.solutionPage.winterFilmName || 'Film solaire hiver',
      eff: 0.0477,
      cpp: 400,
      isFeatured: false,
    },
    {
      id: 'curtains',
      name: t.solutionPage.curtainsName || 'Rideaux',
      eff: 0.01,
      cpp: 50,
      isFeatured: false,
    }
  ]), [t]);

  const results = useMemo(() => {
    const annualBill = monthlyBill[0] * 12;

    const calculateSolution = (sol) => {
      const totalCost = sol.cpp * numWindows[0];
      const annualSavings = annualBill * sol.eff;
      const roiYears = annualSavings > 0 ? totalCost / annualSavings : Infinity;
      const netSavingsForDuration = (annualSavings * roiDuration[0]) - totalCost;
      const roiPercentageForDuration = totalCost > 0 ? (netSavingsForDuration / totalCost) * 100 : (netSavingsForDuration > 0 ? Infinity : 0);
      return { ...sol, totalCost, annualSavings, roiYears, netSavingsForDuration, roiPercentageForDuration };
    };

    return {
      annualBill,
      solutions: solutionsData.map(calculateSolution),
    };
  }, [numWindows, monthlyBill, roiDuration, solutionsData]);

  return (
    <section className="py-24 bg-[#050505]">
      <div className="max-w-6xl mx-auto px-4">
        <Reveal>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">
              {t.solutionPage.savingsTitle || "Calculez vos Économies Potentielles"}
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              {t.solutionPage.savingsSubtitle || "Ajustez les curseurs selon votre situation pour estimer le retour sur investissement et les économies d'énergie avec la technologie SAILECtech."}
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <Reveal className="lg:col-span-1">
            <div className="p-6 rounded-2xl border border-white/10 bg-[#111] space-y-8 sticky top-24">
              <div>
                <label className="flex justify-between items-center text-sm font-bold mb-4">
                  <span className="text-gray-300 flex items-center gap-2"><Layers size={16} /> {t.solutionPage.savingsWindows || 'Nombre de fenêtres'}</span>
                  <span className="px-2 py-1 rounded bg-orange-500/10 text-orange-400 font-mono">{numWindows[0]}</span>
                </label>
                <Slider value={numWindows} onValueChange={setNumWindows} min={1} max={50} step={1} className="w-full" />
              </div>
              <div>
                <label className="flex justify-between items-center text-sm font-bold mb-4">
                  <span className="text-gray-300 flex items-center gap-2"><Zap size={16} /> {t.solutionPage.savingsBill || 'Facture d\'électricité mensuelle'}</span>
                  <span className="px-2 py-1 rounded bg-orange-500/10 text-orange-400 font-mono">{monthlyBill[0]}$</span>
                </label>
                <Slider value={monthlyBill} onValueChange={setMonthlyBill} min={50} max={1000} step={10} className="w-full" />
              </div>
              <div>
                <label className="flex justify-between items-center text-sm font-bold mb-4">
                  <span className="text-gray-300 flex items-center gap-2"><Calendar size={16} /> {t.solutionPage.savingsDuration || 'Durée de calcul'}</span>
                  <span className="px-2 py-1 rounded bg-orange-500/10 text-orange-400 font-mono">{roiDuration[0]} ans</span>
                </label>
                <Slider value={roiDuration} onValueChange={setRoiDuration} min={1} max={20} step={1} className="w-full" />
              </div>
              <div className="pt-4 border-t border-white/10 text-center">
                <p className="text-sm text-gray-400">{t.solutionPage.savingsAnnualBill || 'Facture annuelle estimée'}</p>
                <p className="text-2xl font-bold text-white">{results.annualBill.toFixed(0)}$</p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-2">
            <Reveal>
              <SavingsComparisonChart solutions={results.solutions} t={t} duration={roiDuration[0]} />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- COMPOSANT PRINCIPAL ---

export const SolutionPage = () => {
  const { t } = useLanguage();
  const [hoveredStep, setHoveredStep] = useState(null);

  return (
    <div className="animate-in fade-in duration-70 pt-20" data-testid="solution-page">

      {/* Custom CSS */}
      <style>{`
        @keyframes shine {
          0% { transform: translateX(-100%) rotate(45deg); }
          100% { transform: translateX(100%) rotate(45deg); }
        }
        .group-hover\\:animate-shine:hover {
          animation: shine 1.5s ease-in-out;
        }
        @keyframes moveToApp {
          0% { top: 15%; left: 50%; opacity: 1; }
          50% { top: 47%; left: 33%; opacity: 0.8; }
          100% { top: 80%; left: 15%; opacity: 0; }
        }
        @keyframes moveToFilter {
          0% { top: 80%; left: 15%; opacity: 1; }
          50% { top: 80%; left: 50%; opacity: 0.8; }
          100% { top: 80%; left: 85%; opacity: 0; }
        }
        @keyframes moveToApi {
          0% { top: 80%; left: 85%; opacity: 1; }
          50% { top: 47%; left: 67%; opacity: 0.8; }
          100% { top: 15%; left: 50%; opacity: 0; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(231, 165, 95, 0.3); }
          50% { box-shadow: 0 0 40px rgba(231, 165, 95, 0.6); }
        }
        @keyframes wave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
        .animate-wave { animation: wave 3s ease-in-out infinite; }
      `}</style>

      {/* 1. INTRO & SCHEMATIC */}
      <section className="min-h-screen flex flex-col justify-center py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,_rgba(231,165,95,0.08),_transparent_60%)]"></div>
        <div className="max-w-7xl mx-auto px-4 w-full relative z-10 text-center">
          <Reveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              {t.solutionPage.introTitle1} <span className="text-transparent bg-clip-text" style={{ color: theme.colors.primary }}>{t.solutionPage.introTitle2}</span>.
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-16">
              {t.solutionPage.introSubtitle}
            </p>
          </Reveal>

          <div className="relative h-[400px] md:h-[500px] w-full max-w-4xl mx-auto flex items-center justify-center">
            {/* SVG lines connecting nodes - responsive */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ zIndex: 0 }}>
              <line x1="50" y1="15" x2="15" y2="80" stroke={theme.colors.border} strokeWidth="0.3" strokeDasharray="1,1" />
              <line x1="50" y1="15" x2="85" y2="80" stroke={theme.colors.border} strokeWidth="0.3" strokeDasharray="1,1" />
              <line x1="15" y1="80" x2="85" y2="80" stroke={theme.colors.border} strokeWidth="0.3" strokeDasharray="1,1" />
            </svg>

            {/* Animated circles - using CSS animations for better responsiveness */}
            <div className="absolute top-[15%] left-1/2 w-2 h-2 md:w-3 md:h-3 rounded-full animate-pulse" style={{ backgroundColor: theme.colors.primary, animation: 'moveToApp 3s ease-in-out infinite' }}></div>
            <div className="absolute top-[15%] left-1/2 w-2 h-2 md:w-3 md:h-3 rounded-full animate-pulse" style={{ backgroundColor: theme.colors.accent, animation: 'moveToFilter 3s ease-in-out infinite 1s' }}></div>
            <div className="absolute top-[15%] left-1/2 w-2 h-2 md:w-3 md:h-3 rounded-full animate-pulse bg-white" style={{ backgroundColor: theme.colors.secondary, animation: 'moveToApi 3s ease-in-out infinite 2s' }}></div>

            {/* Nodes */}
            <div className="absolute top-[5%] left-1/2 -translate-x-1/2 flex flex-col items-center group" style={{ '--hover-color': theme.colors.primary }}>
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-[#111] border border-gray-700 flex items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.1)] group-hover:border-[var(--hover-color)] transition-all duration-500 z-10"><CloudCog className="w-8 h-8 md:w-10 md:h-10 text-gray-400 group-hover:text-[var(--hover-color)] transition-colors" /></div>
              <h3 className="mt-2 md:mt-4 text-lg md:text-2xl font-bold text-white">{t.solutionPage.nodeApi}</h3>
              <p className="text-[10px] md:text-sm font-mono tracking-widest uppercase" style={{ color: theme.colors.primary }}>{t.solutionPage.nodeApiSub}</p>
            </div>
            <div className="absolute bottom-[5%] left-[5%] md:left-[10%] flex flex-col items-center group" style={{ '--hover-color': theme.colors.accent }}>
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-[#111] border border-gray-700 flex items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.1)] group-hover:border-[var(--hover-color)] transition-all duration-500 z-10"><Smartphone className="w-8 h-8 md:w-10 md:h-10 text-gray-400 group-hover:text-[var(--hover-color)] transition-colors" /></div>
              <h3 className="mt-2 md:mt-4 text-lg md:text-2xl font-bold text-white">{t.solutionPage.nodeApp}</h3>
              <p className="text-[10px] md:text-sm font-mono tracking-widest uppercase" style={{ color: theme.colors.accent }}>{t.solutionPage.nodeAppSub}</p>
            </div>
            <div className="absolute bottom-[5%] right-[5%] md:right-[10%] flex flex-col items-center group" style={{ '--hover-color': theme.colors.secondary }}>
              <div className="w-16 h-16 md:w-24 md:h-24 rounded-full bg-[#111] border border-gray-700 flex items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.1)] group-hover:border-[var(--hover-color)] transition-all duration-500 z-10"><Layers className="w-8 h-8 md:w-10 md:h-10 text-gray-400 group-hover:text-[var(--hover-color)] transition-colors" /></div>
              <h3 className="mt-2 md:mt-4 text-lg md:text-2xl font-bold text-white">{t.solutionPage.nodeFilter}</h3>
              <p className="text-[10px] md:text-sm font-mono tracking-widest uppercase" style={{ color: theme.colors.secondary }}>{t.solutionPage.nodeFilterSub}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. LE FILTRE */}
      <section className="py-32 border-t relative overflow-hidden" style={{ border: theme.colors.border }}>
        {/* Effets de fond (Glow) */}
        <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <Reveal>
            {/* HEADER : Tout aligné à gauche, empilé verticalement */}
            <div className="text-left mb-20">

              {/* 1. Le Badge (Placé AVANT le titre) */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-1 rounded-full" style={{ backgroundColor: theme.colors.secondary }}></div>
                <span className="text-gray-400 font-mono text-sm md:text-lg uppercase tracking-widest">
                  {t.solutionPage.filterBadge}
                </span>
              </div>

              {/* 2. Le Titre et la description */}
              <div className="max-w-3xl">
                <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
                  {t.solutionPage.filterTitle1} <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-100 to-gray-400">
                    {t.solutionPage.filterTitle2}
                  </span>
                </h2>
                <p className="text-xl text-gray-400 leading-relaxed font-light mt-6 max-w-2xl">
                  {t.solutionPage.filterDesc}
                </p>
              </div>

            </div>
          </Reveal>

          {/* 2. Les Statistiques (Reste inchangé mais propre) */}
          <Reveal delay={100}>
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 mb-24 pb-12 border-b border-white/5">
              <div className="text-center group cursor-default" style={{ '--hover-color': theme.colors.primary }}>
                <div className="text-5xl md:text-7xl font-black text-white mb-2 tracking-tighter group-hover:text-[var(--hover-color)] transition-colors">98<span className="text-3xl align-top">%</span></div>
                <div className="text-sm text-gray-500 font-bold uppercase tracking-widest">{t.solutionPage.statUV}</div>
              </div>
              <div className="w-px h-24 bg-white/10 hidden md:block"></div>
              <div className="text-center group cursor-default" style={{ '--hover-color': theme.colors.secondary }}>
                <div className="text-5xl md:text-7xl font-black text-white mb-2 tracking-tighter group-hover:text-[var(--hover-color)] transition-colors">80<span className="text-3xl align-top">%</span></div>
                <div className="text-sm text-gray-500 font-bold uppercase tracking-widest">{t.solutionPage.statIR}</div>
              </div>
              <div className="w-px h-24 bg-white/10 hidden md:block"></div>
              <div className="text-center group cursor-default" style={{ '--hover-color': theme.colors.accent }}>
                <div className="text-5xl md:text-7xl font-black text-white mb-2 tracking-tighter group-hover:text-[var(--hover-color)] transition-colors">5<span className="text-3xl align-top">min</span></div>
                <div className="text-sm text-gray-500 font-bold uppercase tracking-widest">{t.solutionPage.statInstall}</div>
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-8 mb-24">
            <Reveal delay={200}>
              <div className="bg-[#111] p-6 md:p-10 rounded-[2rem] border border-white/10 h-full relative overflow-hidden group hover:border-orange-500/30 transition-colors duration-500">
                {/* Background icon without animation */}
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-30 transition-opacity duration-500">
                  <RefreshCw size={120} />
                </div>
                {/* Static glow effect */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                <div className="w-12 h-12 md:w-14 md:h-14 bg-orange-500/10 rounded-2xl flex items-center justify-center text-orange-500 mb-6 md:mb-8 border border-orange-500/20">
                  <RefreshCw size={24} className="md:w-7 md:h-7" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{t.solutionPage.reverseTitle}</h3>
                <p className="text-gray-400 leading-relaxed text-sm md:text-base">{t.solutionPage.reverseDesc}</p>

                {/* Animated list items */}
                <ul className="mt-6 space-y-3">
                  <li className="flex items-center gap-3 text-sm text-gray-300 group/item">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    <span className="group-hover/item:text-orange-400 transition-colors">{t.solutionPage.reverseSummer}</span>
                  </li>
                  <li className="flex items-center gap-3 text-sm text-gray-300 group/item">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: theme.colors.secondary }}></span>
                    <span className="group-hover/item:text-blue-400 transition-colors">{t.solutionPage.reverseWinter}</span>
                  </li>
                </ul>

                {/* Animated bottom bar */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div className="bg-[#111] p-6 md:p-10 rounded-[2rem] border border-white/10 h-full relative overflow-hidden group hover:border-blue-500/30 transition-colors duration-500">
                {/* Background icon without animation */}
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-30 transition-opacity duration-500">
                  <Eye size={120} />
                </div>
                {/* Static glow effect */}
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ backgroundColor: `${theme.colors.secondary}33` }}></div>

                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center mb-6 md:mb-8 border transition-transform duration-500" style={{ backgroundColor: `${theme.colors.secondary}1a`, borderColor: `${theme.colors.secondary}33`, color: theme.colors.secondary }}>
                  <Maximize2 size={24} className="md:w-7 md:h-7" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{t.solutionPage.transparencyTitle}</h3>
                <p className="text-gray-400 leading-relaxed text-sm md:text-base">{t.solutionPage.transparencyDesc}</p>

                {/* Static visibility bars */}
                <div className="mt-8 p-4 bg-black/40 rounded-xl border border-white/5 flex items-center justify-between">
                  <span className="text-xs md:text-sm font-bold text-gray-500">{t.solutionPage.visibility}</span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div
                        key={i}
                        className="w-6 md:w-8 h-2 rounded-full"
                        style={{ backgroundColor: theme.colors.secondary, opacity: 0.8 }}
                      ></div>
                    ))}
                  </div>
                </div>

                {/* Animated bottom bar */}
                <div className="absolute bottom-0 left-0 w-full h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" style={{ background: `linear-gradient(to right, ${theme.colors.secondary}, #60a5fa)` }}></div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={400}>
            <div className="bg-gradient-to-br from-[#111] to-[#0a0a0a] rounded-[2rem] border border-white/10 p-8 md:p-12 relative">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-white mb-4">{t.solutionPage.comparisonTitle}</h3>
                <p className="text-gray-500">{t.solutionPage.comparisonSubtitle}</p>
              </div>
              <div className="overflow-x-auto pb-4">
                <div className="min-w-[800px] lg:min-w-full space-y-4">
                  <div className="grid grid-cols-5 gap-4 pb-4 border-b border-white/10 text-xs font-bold uppercase tracking-widest text-gray-500 px-4 items-end">
                    <div className="col-span-1">{t.solutionPage.critere}</div>
                    <div className="col-span-1 text-white">{t.solutionPage.sailectech}</div>
                    <div className="col-span-1">{t.solutionPage.colStore}</div>
                    <div className="col-span-1">{t.solutionPage.colFilm}</div>
                    <div className="col-span-1">{t.solutionPage.colWindow}</div>
                  </div>
                  <ComparisonItem
                    icon={<Clock size={18} />}
                    label={t.solutionPage.compInstallation}
                    sailec={t.solutionPage.compInstallSailec}
                    store={t.solutionPage.compInstallStore}
                    storeOk={false}
                    film={t.solutionPage.compInstallFilm}
                    filmOk={false}
                    windowItem={t.solutionPage.compInstallWindow}
                    windowOk={false}
                  />
                  <ComparisonItem
                    icon={<RefreshCw size={18} />}
                    label={t.solutionPage.compSeason}
                    sailec={t.solutionPage.compSeasonSailec}
                    store={t.solutionPage.compSeasonStore}
                    storeOk={false}
                    film={t.solutionPage.compSeasonFilm}
                    filmOk={false}
                    windowItem={t.solutionPage.compSeasonWindow}
                    windowOk={true}
                  />
                  <ComparisonItem
                    icon={<Wallet size={18} />}
                    label={t.solutionPage.compCost}
                    sailec={t.solutionPage.compCostSailec}
                    store={t.solutionPage.compCostStore}
                    storeOk={true}
                    film={t.solutionPage.compCostFilm}
                    filmOk={true}
                    windowItem={t.solutionPage.compCostWindow}
                    windowOk={false}
                  />
                  <ComparisonItem
                    icon={<Wind size={18} />}
                    label={t.solutionPage.compHeat}
                    sailec={t.solutionPage.compHeatSailec}
                    store={t.solutionPage.compHeatStore}
                    storeOk={false}
                    film={t.solutionPage.compHeatFilm}
                    filmOk={true}
                    windowItem={t.solutionPage.compHeatWindow}
                    windowOk={true}
                  />
                  <ComparisonItem
                    icon={<Eye size={18} />}
                    label={t.solutionPage.compVisibility}
                    sailec={t.solutionPage.compVisibilitySailec}
                    store={t.solutionPage.compVisibilityStore}
                    storeOk={false}
                    film={t.solutionPage.compVisibilityFilm}
                    filmOk={true}
                    windowItem={t.solutionPage.compVisibilityWindow}
                    windowOk={true}
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5. CALCULATEUR D'ÉCONOMIES */}
      <SavingsCalculatorSection t={t} />

      {/* SEPARATOR - Composante 02 */}
      <section className="bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 py-8 border-t border-white/5 flex items-center justify-end gap-4">
          <span className="text-gray-400 font-mono text-xs md:text-sm uppercase tracking-widest">{t.solutionPage.appBadge}</span>
          <div className="w-8 h-1 rounded-full bg-orange-500"></div>
        </div>
      </section>

      {/* 3. L'APPLICATION */}
      <section className="pb-24 pt-12 relative bg-[#050505]">
        <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-right mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">{t.solutionPage.appTitle}</h2>
              <button className="px-6 py-2 rounded-full border border-gray-700 text-gray-500 cursor-not-allowed text-sm font-mono hover:bg-white/5 transition-colors">{t.solutionPage.appComingSoon}</button>
            </div>
          </Reveal>

          {/* Onboarding Steps with hover effect */}
          <div className="mb-32 ">
            <Reveal><h3 className="text-2xl font-bold text-white mb-8 border-l-4 pl-4" style={{ borderColor: theme.colors.secondary }}>{t.solutionPage.onboardingTitle}</h3></Reveal>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5].map((step) => (
                <StepCard
                  key={step}
                  step={step}
                  t={t}
                  isHovered={hoveredStep === step}
                  anyHovered={hoveredStep !== null}
                  onHover={() => setHoveredStep(step)}
                  onLeave={() => setHoveredStep(null)}
                />
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-16">
            <Reveal>
              <h3 className="text-2xl font-bold text-white mb-6 border-l-4 pl-4" style={{ borderColor: theme.colors.primary }}>{t.solutionPage.dashboardTitle}</h3>
              <p className="text-gray-400 mb-6">{t.solutionPage.dashboardDesc}</p>
              <div className="h-[500px] w-[225px] max-w-sm mx-auto">
                <PlaceholderScreen
                  title={t.solutionPage.dashboardMain}
                  subtitle={t.solutionPage.dashboardSub}
                  color={theme.colors.border}
                  icon={<BarChart3 size={40} />}
                  imageSrc="/images/app-screenshots/dashboard.jpg"
                />
              </div>
            </Reveal>
            <Reveal delay={200}>
              <h3 className="text-2xl font-bold text-white mb-6 border-l-4 pl-4" style={{ borderColor: theme.colors.accent }}>{t.solutionPage.zoneTitle}</h3>
              <p className="text-gray-400 mb-6">{t.solutionPage.zoneDesc}</p>
              <div className="h-[500px] w-[225px] max-w-sm mx-auto">
                <PlaceholderScreen
                  title={t.solutionPage.zonePlan}
                  subtitle={t.solutionPage.zoneSub}
                  color={theme.colors.border}
                  icon={<LayoutTemplate size={40} />}
                  imageSrc="/images/app-screenshots/zone-control.jpg"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 4. API */}
      <section className="py-24 border-t relative overflow-hidden mb-16" style={{ border: theme.colors.border }}>
        <div className="max-w-5xl mx-auto px-4 relative z-10 right-0">
          <Reveal>
            {/* 1. Le Badge (Placé AVANT le titre) */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-1 rounded-full" style={{ backgroundColor: theme.colors.primary }}></div>
              <span className="text-gray-400 font-mono text-sm md:text-lg uppercase tracking-widest">
                {t.solutionPage.apiBadge}
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400">{t.solutionPage.apiTitle}</h2>
            <p className="text-xl text-gray-400 leading-relaxed mb-12">{t.solutionPage.apiDesc}</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8 text-left">
            <ApiCard icon={<CloudCog />} title={t.solutionPage.apiInputTitle} desc={t.solutionPage.apiInputDesc} />
            <ApiCard icon={<Cpu />} title={t.solutionPage.apiCalcTitle} desc={t.solutionPage.apiCalcDesc} highlight />
            <ApiCard icon={<Share2 />} title={t.solutionPage.apiAutoTitle} desc={t.solutionPage.apiAutoDesc} />
          </div>
        </div>
      </section>

      {/* 5. RECAP */}
      <section className="py-20 bg-black border-t border-white/10 mb-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal><h2 className="text-3xl md:text-5xl font-bold mb-16 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-400 text-center">{t.solutionPage.recapTitle}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-1 bg-[#111] border border-white/10 rounded-3xl overflow-hidden">
            <div className="p-10 hover:bg-white/5 transition-colors group text-center md:text-left">
              <div className="font-bold text-xl mb-4 group-hover:scale-110 transition-transform origin-left" style={{ color: theme.colors.primary }}>{t.solutionPage.recap1Num}</div>
              <h3 className="text-2xl text-white font-bold mb-2">{t.solutionPage.recap1Title}</h3>
              <p className="text-gray-500">{t.solutionPage.recap1Desc}</p>
            </div>
            <div className="p-10 hover:bg-white/5 transition-colors group text-center md:text-left border-t md:border-t-0 md:border-l border-white/5">
              <div className="font-bold text-xl mb-4 group-hover:scale-110 transition-transform origin-left" style={{ color: theme.colors.secondary }}>{t.solutionPage.recap2Num}</div>
              <h3 className="text-2xl text-white font-bold mb-2">{t.solutionPage.recap2Title}</h3>
              <p className="text-gray-500">{t.solutionPage.recap2Desc}</p>
            </div>
            <div className="p-10 hover:bg-white/5 transition-colors group text-center md:text-left border-t md:border-t-0 md:border-l border-white/5">
              <div className="font-bold text-xl mb-4 group-hover:scale-110 transition-transform origin-left" style={{ color: theme.colors.accent }}>{t.solutionPage.recap3Num}</div>
              <h3 className="text-2xl text-white font-bold mb-2">{t.solutionPage.recap3Title}</h3>
              <p className="text-gray-500">{t.solutionPage.recap3Desc}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
